import React, { ReactNode } from 'react';
import type { MessageTranslator } from '@oneflowab/pomes';

import ModalForm from '@app/hocs/modal-form';

import Field from '@app/components/field';
import SelectField from '@app/components/select-field';
import TextField from '@app/components/text-field';
import Toggle from '@app/components/toggle';
import { T, t } from '@app/components/translation';
import * as EditableForm from '@app/components/editable-form';

import style from './add-automation-rule.module.scss';
import { useDaysValidations } from '@flow/libs/data-validators/automation-rule-validator';

export type FormData = Pick<Oneflow.AutomationRule, 'key' | 'workspaceId' | 'days' | 'active'>;

export type Props = {
  createAutomationRule: (formData: FormData) => void;
  createState: any;
  resetCreateState: () => void;
  availableKeys?: {
    label: string;
    value: string;
  }[];
  children?: ReactNode;
  message: MessageTranslator;
};

export const AddAutomationRule: React.FC<Props> = ({
  createAutomationRule,
  createState,
  resetCreateState,
  availableKeys,
  children,
  message,
}) => {
  const daysValidations = useDaysValidations();
  const renderBody = () => {
    const isDisabled = availableKeys?.length === 0;
    let placeholder = message({
      id: 'Select rule',
      comment: 'Rule key to create automation rule.',
    });
    if (isDisabled) {
      placeholder = message({
        id: 'There are no more rules to create',
        comment: 'Rule key to create automation rule.',
      });
    }
    return (
      <div>
        <Field
          name="key"
          label={t('Rule', {
            _comment: 'Label for the automation rule key when creating a rule',
          })}
          placeholder={placeholder}
          component={SelectField}
          options={availableKeys}
          clearable={false}
          searchable={false}
          required
          disabled={isDisabled}
        />
        <div>
          <EditableForm.Label>
            <T
              _str="Active"
              _comment="Label for automation rule attribute."
            />
          </EditableForm.Label>
          <Field
            name="active"
            type="checkbox"
            label="Active"
            hideLabel
            render={({ input }) => (
              <Toggle
                className={style.Toggle}
                version="latest"
                isControlled
                checked={input.value || true}
                onChange={input.onChange}
                disabled={isDisabled}
              />
            )}
          />
        </div>

        <Field
          name="days"
          label={t('Trigger after (days)', {
            _comment: 'Label for the automation rule key when creating a rule',
          })}
          placeholder={t('Enter number of days', {
            _comment: 'Placeholder for the number of days input in automation rule creation',
          })}
          component={TextField}
          required
          disabled={isDisabled}
          validate={daysValidations}
        />
      </div>
    );
  };

  return (
    <ModalForm
      title={
        <T
          _str="Create rule"
          _comment="The text shown in the button for creating a new automation rule"
        />
      }
      body={renderBody()}
      onSubmit={createAutomationRule}
      resetFormState={resetCreateState}
      formState={createState}
      modalKey="create rule modal"
    >
      {children}
    </ModalForm>
  );
};
